import { STATUSES } from '@/constants';
import { PaymentOption } from '@/models/company';
import { Contact } from '@/models/contact';
import { Credit } from '@/models/credit';
import { CRMLinking, Customer, CustomerGroup, PaymentActivation, ServiceZone, Zone } from '@/models/customer';
import { CustomerContact } from '@/models/customerContact';
import { CreditResponse, CreditStatus } from '@/services/api/models/credit';
import { money } from '@/utils/i18n';

import { CRMState } from './state';

// ---------------------------------------------------------
// CUSTOMERS
// ---------------------------------------------------------

export const UPDATE_IS_LOADING_CUSTOMERS = (state: CRMState, value: boolean) => {
  state.isLoadingCustomers = value;
};

export const SET_CUSTOMERS = (state: CRMState, value: Customer[]) => {
  state.customers = value;
};

export const SAVE_CUSTOMER = (state: CRMState, customer: Customer) => {
  const index = state.customers.findIndex((c) => c.id === customer.id);

  if (!customer.displayName) {
    customer.displayName = customer.nickname || customer.name;
  }

  if (!~index) {
    state.customers.push(customer);
  } else {
    state.customers.splice(index, 1, customer);
  }
};

export const SET_STATUSES = (state: CRMState, statuses) => {
  state.statuses = statuses;
};

export const SET_STATS = (state: CRMState, stats) => {
  state.stats = stats;
};

export const SET_TAGS = (state: CRMState, tags) => {
  state.tags = tags;
};

export const SET_TIERS = (state: CRMState, tiers) => {
  state.tiers = tiers;
};

export const SET_LICENSE_TYPES = (state: CRMState, licenseTypes) => {
  state.licenseTypes = licenseTypes;
};

// ---------------------------------------------------------
// CONTACTS
// ---------------------------------------------------------

export const SET_CONTACTS = (state: CRMState, value: Contact[]) => {
  state.contacts = value;
};

export const DELETE_CONTACT = (state: CRMState, value: Contact) => {
  state.contacts = state.contacts.filter((c) => c.id !== value.id);
};

export const SAVE_CONTACT = (state: CRMState, value: Contact) => {
  const index = state.contacts.findIndex((contact) => contact.id === value.id);

  if (!~index) {
    state.contacts.unshift(value);
  } else {
    state.contacts.splice(index, 1, value);
  }
};

export const SET_CONTACTS_LOADING = (state: CRMState, loading: boolean) => {
  state.isLoadingContacts = loading;
};

export const SET_CUSTOMER_CONTACTS = (state: CRMState, value: CustomerContact[]) => {
  state.customerContacts = value;
};

export const SET_CUSTOMER_CONTACTS_LOADING = (state: CRMState, loading: boolean) => {
  state.isLoadingCustomerContacts = loading;
};

// ---------------------------------------------------------
// ZONES
// ---------------------------------------------------------

export const SET_ZONES = (state: CRMState, zones: Zone[]) => {
  state.zones = zones; // todo: remove this. These are set on the company level -
};

export const SET_ZONES_LOADING = (state: CRMState, loading: boolean) => {
  state.isLoadingZones = loading;
};

export const SAVE_ZONE = (state: CRMState, zone: Zone) => {
  const idx = state.zones.findIndex((z) => z.id === zone.id);

  if (!~idx) {
    state.zones.push(zone);
  } else {
    state.zones.splice(idx, 1, zone);
  }
};

export const REMOVE_ZONE = (state: CRMState, zone: Zone) => {
  state.zones.splice(state.zones.indexOf(zone), 1);
};

export const SET_ZIPS = (state: CRMState, zips) => {
  state.zips = zips;
};

export const SET_SERVICE_ZONES = (state: CRMState, serviceZones: ServiceZone[]) => {
  state.serviceZones = serviceZones;
};

export const SAVE_SERVICE_ZONE = (state: CRMState, serviceZone: ServiceZone) => {
  const idx = state.serviceZones.findIndex((z) => z.id === serviceZone.id);

  if (!~idx) {
    state.serviceZones.push(serviceZone);
  } else {
    state.serviceZones.splice(idx, 1, serviceZone);
  }
};

export const REMOVE_SERVICE_ZONE = (state: CRMState, serviceZone: ServiceZone) => {
  state.serviceZones.splice(state.serviceZones.indexOf(serviceZone), 1);
};

// ---------------------------------------------------------
// PRICE SCHEDULES
// ---------------------------------------------------------

export const UPDATE_CUSTOMERS_PRICE_SCHEDULE = (state: CRMState, customers: Customer[]) => {
  customers.forEach((customer) => {
    const index = state.customers.findIndex((stateCustomer) => stateCustomer.id === customer.id);

    if (state.customers[index]) {
      state.customers[index].priceScheduleId = customer.priceScheduleId;
    }
  });
};

export const SET_PAYMENT_TERMS = (state: CRMState, paymentTerms) => {
  state.paymentTerms = paymentTerms;
};

// ---------------------------------------------------------
// TASKS
// ---------------------------------------------------------

export const SET_TASKS = (state: CRMState, tasks) => {
  state.tasks = tasks;
};

export const SET_TASK = (state: CRMState, task) => {
  state.tasks.push(task);
};

export const SET_TASKS_LOADING = (state: CRMState, loading: boolean) => {
  state.isLoadingTasks = loading;
};

export const SAVE_TASK = (state: CRMState, task) => {
  const index = state.tasks.findIndex((t) => t.id === task.id);

  if (!~index) {
    state.tasks.unshift(task);
  } else {
    state.tasks.splice(index, 1, task);
  }
};

// ---------------------------------------------------------
// CREDITS
// ---------------------------------------------------------

export const SET_CREDITS = (state: CRMState, creditResponses: CreditResponse[]) => {
  const credits: Credit[] = creditResponses.map((creditResp) => {
    return {
      ...creditResp,
      displayName: `${money({ amount: creditResp.amount.amount })} • ${creditResp.reasonDisplay}`,
      internalStatus: creditResp.appliedTo ? STATUSES.CREDIT_INTERNAL.APPLIED : creditResp.status,
    };
  });
  state.credits = credits;
};

export const SAVE_CREDIT = (state: CRMState, credit: Credit) => {
  const index = state.credits.findIndex((cr) => cr.id === credit.id);

  if (!~index) {
    state.credits.unshift(credit);
  } else {
    state.credits.splice(index, 1, credit);
  }
};

export const ARCHIVE_CREDIT = (state: CRMState, credit: Credit) => {
  const currentCredit = state.credits.filter((c) => c.id === credit.id);

  currentCredit[0].archived = true;
  currentCredit[0].status = CreditStatus.CANCELLED;
};

export const LOADING_CREDITS = (state: CRMState, value: boolean) => (state.isLoadingCredits = value);

export const LOADING_GROUPS = (state: CRMState, value: boolean) => (state.isLoadingGroups = value);

export const SET_GROUPS = (state: CRMState, groups: CustomerGroup[]) => {
  state.customerGroups = groups;
};

export const SAVE_GROUP = (state: CRMState, group: CustomerGroup) => {
  const index = state.customerGroups.findIndex((g) => g.id === group.id);

  if (!~index) {
    state.customerGroups.unshift(group);
  } else {
    state.customerGroups.splice(index, 1, group);
  }
};

export const DELETE_GROUP = (state: CRMState, group: CustomerGroup) => {
  const index = state.customerGroups.findIndex((g) => g.id === group.id);

  state.customerGroups.splice(index, 1);
};

// ---------------------------------------------------------
// CRM LINKING
// ---------------------------------------------------------

export const SET_CRM_LINKINGS = (state: CRMState, crmLinkings: CRMLinking[]) => {
  state.crmLinkings = crmLinkings;
};

export const SAVE_CRM_LINKING = (state: CRMState, crmLinking: CRMLinking) => {
  state.crmLinkings.splice(state.crmLinkings.indexOf(crmLinking), 1);
};

// ---------------------------------------------------------
// PAYMENT_OPTIONS
// ---------------------------------------------------------

export const SET_CUSTOMER_PAYMENT_OPTIONS = (state: CRMState, value: PaymentOption[]) => {
  state.paymentOptions = value;
};

// ---------------------------------------------------------
// PAYMENT_ACTIVATION
// ---------------------------------------------------------

export const SAVE_CUSTOMER_PAYMENT_ACTIVATION = (state: CRMState, activatedPaymentsCustomers: PaymentActivation[]) => {
  state.activatedPaymentsCustomers = activatedPaymentsCustomers;
};
